import React, { useState, useEffect, useRef } from "react";
import {
  Menu,
  Activity,
  Settings,
  User,
  LogOut,
  ChevronDown,
  UserCircle,
} from "lucide-react";
import { useAppStore } from "../../store/useAppStore";
import { useAuth } from "../../hooks/useAuth";
import { useAppNavigate } from "../../hooks/useAppNavigate";
import { LoadingSpinner } from "./LoadingSpinner";

export const Header: React.FC = () => {
  const { sidebarOpen, setSidebarOpen, loading } = useAppStore();
  const { user, logout, isAdmin, isFleetScoped } = useAuth();
  const navigateToView = useAppNavigate();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setUserMenuOpen(false);
      }
    };

    if (userMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [userMenuOpen]);

  return (
    <header className="bg-white shadow-sm border-b border-gray-200 px-4 py-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          {!isFleetScoped && (
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="p-2 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
              aria-label="Toggle sidebar"
            >
              <Menu className="w-5 h-5" />
            </button>
          )}

          <div className="flex items-center space-x-2">
            {isFleetScoped ? (
              <button
                type="button"
                onClick={() => navigateToView("map")}
                className="text-xl font-semibold text-gray-900 hover:text-primary-600 transition-colors"
                title="Back to Live Map"
              >
                Vehicle Tracker
              </button>
            ) : (
              <h1 className="text-xl font-semibold text-gray-900">
                Vehicle Tracker
              </h1>
            )}
            {loading && <LoadingSpinner size="sm" />}
          </div>
        </div>

        <div className="flex items-center space-x-2">
          {!isFleetScoped && (
            <a
              href="/status"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="System status (opens in a new tab)"
              title="System status"
              className="p-2 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
            >
              <Activity className="w-5 h-5" />
            </a>
          )}

          {isAdmin && (
            <button
              type="button"
              onClick={() => navigateToView("settings")}
              aria-label="Settings"
              title="Settings"
              className="p-2 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
            >
              <Settings className="w-5 h-5" />
            </button>
          )}

          {/* User Menu */}
          <div className="relative" ref={menuRef}>
            <button
              onClick={() => setUserMenuOpen(!userMenuOpen)}
              className="flex items-center space-x-2 p-2 rounded-md text-gray-700 hover:text-gray-900 hover:bg-gray-100 transition-colors"
            >
              <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                <User className="w-4 h-4 text-blue-600" />
              </div>
              <div className="hidden md:flex flex-col items-start">
                <span className="text-sm font-medium">{user?.full_name}</span>
                <span className="text-xs text-gray-500">
                  {isFleetScoped && user?.fleet_name
                    ? user.fleet_name
                    : isAdmin
                      ? "Admin"
                      : user?.role}
                </span>
              </div>
              <ChevronDown
                className={`w-4 h-4 transition-transform ${userMenuOpen ? "rotate-180" : ""}`}
              />
            </button>

            {/* Dropdown Menu */}
            {userMenuOpen && (
              <div className="absolute right-0 mt-2 min-w-[12rem] w-max max-w-[min(20rem,calc(100vw-2rem))] bg-white rounded-md shadow-lg border border-gray-200 z-50">
                <div className="py-1">
                  <div className="px-4 py-2 border-b border-gray-100">
                    <p className="text-sm font-medium text-gray-900 break-words">
                      {user?.full_name}
                    </p>
                    <p className="text-xs text-gray-500 break-all">
                      {user?.email}
                    </p>
                    {isFleetScoped && user?.fleet_name && (
                      <p className="text-xs text-gray-500 mt-0.5 break-words">
                        {user.fleet_name}
                      </p>
                    )}
                  </div>

                  <button
                    type="button"
                    onClick={() => {
                      navigateToView("profile");
                      setUserMenuOpen(false);
                    }}
                    className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                  >
                    <UserCircle className="w-4 h-4 mr-3 shrink-0" />
                    Profile
                  </button>

                  <button
                    onClick={() => {
                      logout();
                      setUserMenuOpen(false);
                    }}
                    className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                  >
                    <LogOut className="w-4 h-4 mr-3" />
                    Sign out
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};
